import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setTokenGetter } from "./api";

const Ctx = createContext(null);
const DEMO_KEY = "ul_demo";
const readDemo = () => { try { return JSON.parse(localStorage.getItem(DEMO_KEY)); } catch (e) { return null; } };

export function AppProvider({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { user } = useUser();
  const qc = useQueryClient();
  const [demo, setDemo] = useState(readDemo);
  const [lang, setLangState] = useState(() => localStorage.getItem("ul_lang"));
  const setLang = useCallback((l) => { localStorage.setItem("ul_lang", l); setLangState(l); }, []);

  setTokenGetter(async () => (demo ? demo.tokens[demo.as] : isSignedIn ? await getToken() : null));
  const signedIn = !!demo || !!isSignedIn;

  const meQ = useQuery({
    queryKey: ["me", demo?.as || "clerk", demo?.demo_id],
    queryFn: async () => (await api.get("/me")).data,
    enabled: signedIn && (!!demo || isLoaded),
    staleTime: 15000,
  });

  useEffect(() => {
    if (!demo && isSignedIn && user) {
      api.post("/me/sync", { name: user.firstName || user.fullName, email: user.primaryEmailAddress?.emailAddress }).catch(() => {});
    }
  }, [demo, isSignedIn, user]);

  const saveDemo = (d) => { if (d) localStorage.setItem(DEMO_KEY, JSON.stringify(d)); else localStorage.removeItem(DEMO_KEY); setDemo(d); };

  const startDemo = useCallback(async () => {
    const prev = readDemo();
    const { data } = await api.post("/demo/start", { demo_id: prev?.demo_id });
    saveDemo({ ...data, as: "meera" });
    qc.clear();
  }, [qc]);

  const switchTo = useCallback((as) => { saveDemo({ ...readDemo(), as }); qc.clear(); }, [qc]);

  const logout = useCallback(async () => {
    if (demo) saveDemo(null); else await signOut();
    qc.clear();
  }, [demo, signOut, qc]);

  const refresh = useCallback(() => qc.invalidateQueries(), [qc]);

  useEffect(() => {
    const m = meQ.data;
    if (m?.state !== "ready" || !lang || !localStorage.getItem("ul_lang_pending")) return;
    localStorage.removeItem("ul_lang_pending");
    if (m.me.ui_language !== lang) api.patch(`/members/${m.me.id}`, { ui_language: lang }).then(refresh).catch(() => {});
  }, [meQ.data, lang, refresh]);

  const value = useMemo(() => ({
    loading: (!demo && !isLoaded) || (signedIn && meQ.isLoading),
    signedIn, demo, me: meQ.data, meError: meQ.error, refresh, startDemo, switchTo, logout, lang, setLang,
  }), [demo, isLoaded, signedIn, meQ.data, meQ.isLoading, meQ.error, refresh, startDemo, switchTo, logout, lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useApp = () => useContext(Ctx);

export function useData(path, opts = {}) {
  const { demo } = useApp();
  return useQuery({
    queryKey: [path, demo?.as || "clerk"],
    queryFn: async () => (await api.get(path)).data,
    ...opts,
  });
}
